import React, { useState, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { PiArrowsDownUpLight, PiGearLight, PiFunnelLight, PiMagnifyingGlassLight, PiXLight, PiGridFourLight, PiSortAscendingLight, PiLockLight, PiArrowsClockwiseLight, PiTrashLight } from 'react-icons/pi';
import clsx from 'clsx';
import Badge from './Badge';
import Button from './Button';

type FilterOption = {
  value: string;
  label: string;
  checked?: boolean;
  children?: FilterOption[];
  parent_id?: number | null;
};

type FilterSection = {
  id: string;
  name: string;
  options: FilterOption[];
};

type SortOption = {
  value: string;
  label: string;
  current: boolean;
};

export type TabItem = {
  label: string;
  value: string;
  disabled?: boolean;
  tooltip?: string;
  icon?: React.ReactElement;
  lock?: boolean;
};

type FilterAction = {
  label: string;
  onClick: () => void;
  icon?: React.ReactElement;
};

type FilterBarWithTabsProps = {
  tabs?: TabItem[];
  rightTabs?: TabItem[];
  activeTab?: string;
  onTabClick?: (tabValue: string) => void;
  filters?: FilterSection[];
  sortOptions?: SortOption[];
  selectedFilters?: { [key: string]: string[] };
  onFilterChange?: (filterName: string, selectedValues: string[]) => void;
  onSortChange?: (value: string) => void;
  onSearchChange?: (searchQuery: string) => void;
  sortValue?: string;
  actions?: FilterAction[];
  onClearAllFilters?: () => void;
  additionalControls?: React.ReactNode;
  viewMode?: 'grid' | 'table';
  setViewMode?: (mode: 'grid' | 'table') => void;
  onViewModeChange?: (mode: 'grid' | 'table') => void;
  noParentPadding?: boolean;
  noParentHeight?: boolean;
  noBorder?: boolean;
  onRefresh?: () => void;
  isRefreshLoading?: boolean;
  selectionCount?: number;
  itemCount?: number;
  totalItemCount?: number;
  itemCountLabel?: string;
  /** Recycle bin toggle — shows a small bin button on the right side of the
   *  toolbar that flips between active and trashed views. The page owns the
   *  state and is responsible for fetching the right data set when the
   *  toggle changes. */
  recycleBinEnabled?: boolean;
  recycleBinActive?: boolean;
  onRecycleBinToggle?: (active: boolean) => void;
  recycleBinCount?: number;
  /** Resource label rendered as "Back to {recycleBinLabel}" when the bin
   *  view is active (e.g. "Matters", "Teams", "Playbooks"). When off, the
   *  button shows only the bin icon. */
  recycleBinLabel?: string;
};

const FilterBarWithTabs: React.FC<FilterBarWithTabsProps> = ({
  tabs = [],
  rightTabs = [],
  activeTab,
  onTabClick,
  filters = [],
  sortOptions = [],
  selectedFilters = {},
  onFilterChange,
  onSortChange,
  onSearchChange,
  sortValue,
  actions = [],
  onClearAllFilters,
  additionalControls,
  viewMode,
  setViewMode,
  onViewModeChange,
  noParentPadding = false,
  noParentHeight = false,
  noBorder = false,
  onRefresh,
  isRefreshLoading = false,
  selectionCount,
  itemCount,
  totalItemCount,
  itemCountLabel = 'items',
  recycleBinEnabled = false,
  recycleBinActive = false,
  onRecycleBinToggle,
  recycleBinCount,
  recycleBinLabel,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { t } = useTranslation();

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearchChange?.(query);
  };

  const totalActiveFilters = Object.values(selectedFilters).reduce((acc, curr) => acc + curr.length, 0);

  const hasControls = filters.length > 0 || sortOptions.length > 0 || onSearchChange || actions.length > 0 || additionalControls || viewMode || rightTabs.length > 0 || onRefresh;

  const hasTabs = tabs.length > 0 || rightTabs.length > 0;

  const ViewToggle: React.FC = () => {
    if (!viewMode || !setViewMode) return null;
    return (
      <div data-tour-id="matters-view-toggle" className="flex items-center border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700">
        <button
          onClick={() => setViewMode('grid')}
          className={clsx(
            'p-1 rounded-l',
            viewMode === 'grid' ? 'bg-gray-200 dark:bg-gray-600' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
          )}
          title={t('Grid View')}
        >
          <PiGridFourLight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
        </button>
        <button
          onClick={() => setViewMode('table')}
          className={clsx(
            'p-1 rounded-r',
            viewMode === 'table' ? 'bg-gray-200 dark:bg-gray-600' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
          )}
          title={t('Table View')}
        >
          <PiSortAscendingLight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
        </button>
      </div>
    );
  };

  // Helper function to flatten hierarchical options for label map
  const flattenOptions = (options: FilterOption[]): FilterOption[] => {
    return options.flatMap((option) => [option, ...(option.children ? flattenOptions(option.children) : [])]);
  };

  const filterLabelMap = filters.reduce((acc, section) => {
    const flatOptions = flattenOptions(section.options);
    flatOptions.forEach((option) => {
      acc[`${section.id}:${option.value}`] = `${t(section.name)}: ${t(option.label)}`;
    });
    return acc;
  }, {} as Record<string, string>);

  // Helper function to render filter options recursively with proper indentation
  const renderFilterOption = (option: FilterOption, section: FilterSection, depth: number = 0) => {
    const isChecked = selectedFilters[section.id]?.includes(option.value);
    const isParent = option.children && option.children.length > 0;
    const paddingLeft = depth > 0 ? `${depth * 16}px` : '0px';

    // If this is a parent option, render it as a header instead of a clickable item
    if (isParent && depth > 0) {
      return (
        <React.Fragment key={option.value}>
          <div
            className="px-4 py-2 text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600/50 font-medium"
            style={{ paddingLeft: `calc(1rem + ${paddingLeft})` }}
          >
            {t(option.label)}
          </div>
          {option.children && option.children.map((child) => renderFilterOption(child, section, depth + 1))}
        </React.Fragment>
      );
    }

    // If this is a root parent, render it as a header
    if (isParent && depth === 0) {
      return (
        <React.Fragment key={option.value}>
          <div className="px-4 py-2 text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600/50 font-medium">
            {t(option.label)}
          </div>
          {option.children && option.children.map((child) => renderFilterOption(child, section, depth + 1))}
        </React.Fragment>
      );
    }

    // Otherwise, render as a clickable checkbox option
    return (
      <React.Fragment key={option.value}>
        <Menu.Item>
          {({ active }) => (
            <div
              className={`flex items-center py-2 cursor-pointer text-xs transition-colors ${active ? 'bg-primary-50 dark:bg-primary-900/30' : ''}`}
              style={{ paddingLeft: `calc(1rem + ${paddingLeft})`, paddingRight: '1rem' }}
              onClick={() => {
                const current = selectedFilters[section.id] || [];
                const updated = isChecked
                  ? current.filter((v) => v !== option.value)
                  : [option.value]; // Single selection
                onFilterChange?.(section.id, updated);
              }}
            >
              <input
                type="checkbox"
                checked={isChecked}
                readOnly
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span
                className="ml-3 text-gray-700 dark:text-gray-200 truncate"
              >
                {t(option.label)}
              </span>
            </div>
          )}
        </Menu.Item>
        {option.children && option.children.map((child) => renderFilterOption(child, section, depth + 1))}
      </React.Fragment>
    );
  };

  const renderTab = (tab: TabItem) => {
    const isActive = activeTab === tab.value;
    const tabClasses = clsx(
      'flex items-center px-3 py-1 text-xs font-medium rounded transition-all duration-200 border border-1 hover:border-gray-300 hover:bg-white dark:hover:bg-black',
      isActive
        ? 'text-primary-600 dark:text-primary-300 bg-white dark:bg-black border-gray-300'
        : 'text-gray-600 dark:text-gray-300 border-transparent hover:border-gray-300 dark:hover:bg-gray-700'
    );
    const disabledClasses = tab.disabled ? 'opacity-50 cursor-not-allowed' : '';
    return (
      <button
        key={tab.value}
        onClick={() => !tab.disabled && onTabClick?.(tab.value)}
        disabled={tab.disabled}
        title={tab.disabled ? tab.tooltip : ''}
        className={`${tabClasses} ${disabledClasses}`}
      >
        {tab.icon && <span className="mr-1">{tab.icon}</span>}
        {t(tab.label)}
        {tab.lock && (
          <PiLockLight
            className="absolute -top-2 -right-2 text-yellow-500 bg-white dark:bg-gray-800 rounded-full shadow-md p-[2px]"
            size={16}
            title="Premium Feature"
          />
        )}
      </button>
    );
  };

  // Shared button style for filter bar controls
  const controlBtnClass = "flex gap-1 items-center px-2 py-1 border border-gray-300 dark:border-gray-500 bg-transparent text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-400 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-all duration-200 rounded shadow-sm";

  // When tabs are present, filter/sort/search controls move to the right side
  const hasLeftTabs = tabs.length > 0;

  // Reusable filter/sort/search/clear controls block (JSX variable, not a component,
  // to avoid unmount/remount on every re-render which would lose input focus)
  const filterSortSearchControls = (
    <>
      {filters.length > 0 && onFilterChange && (
        <div className="flex items-center gap-2">
          <Menu as="div" className="relative">
            <Menu.Button className={controlBtnClass}>
              <PiFunnelLight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
              <span className="text-black dark:text-white text-xs">{t('Filter')}</span>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Menu.Items className={clsx(
                "absolute mt-2 min-w-max max-w-96 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 z-50 max-h-96 overflow-y-auto custom-scrollbar",
                hasLeftTabs ? "right-0" : "left-0"
              )}>
                {filters.map((section, sectionIndex) => (
                  <div key={section.id} className={sectionIndex > 0 ? 'border-t border-gray-300 dark:border-gray-600' : ''}>
                    <div className="px-4 py-3 text-xs text-gray-800 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 sticky top-0">{t(section.name)}</div>
                    {section.options.map((option) => renderFilterOption(option, section, 0))}
                  </div>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
          {totalActiveFilters > 0 && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(selectedFilters).flatMap(([sectionId, values]) =>
                values.map((value) => (
                  <Badge
                    key={`${sectionId}:${value}`}
                    color="gray"
                    className="text-xs"
                  >
                    {filterLabelMap[`${sectionId}:${value}`] || `${t(sectionId)}: ${t(value)}`}
                  </Badge>
                ))
              )}
            </div>
          )}
        </div>
      )}
      {sortOptions.length > 0 && onSortChange && viewMode === 'grid' && (
        <Menu as="div" className="relative">
          <Menu.Button className={controlBtnClass}>
            <PiArrowsDownUpLight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            <span className="text-black dark:text-white text-xs">{t('Sort')}</span>
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Menu.Items className={clsx(
              "absolute mt-2 w-56 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 z-50",
              hasLeftTabs ? "right-0" : "left-0"
            )}>
              {sortOptions.map((option) => (
                <Menu.Item key={option.value}>
                  {({ active }) => (
                    <div
                      onClick={() => onSortChange(option.value)}
                      className={`p-2 cursor-pointer text-xs text-gray-700 dark:text-gray-200 ${active ? 'bg-gray-100 dark:bg-gray-600' : ''}`}
                    >
                      {t(option.label)}
                    </div>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>
      )}
      {onSearchChange && (
        !isSearchOpen ? (
          <button
            onClick={() => setIsSearchOpen(true)}
            className={controlBtnClass}
          >
            <PiMagnifyingGlassLight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          </button>
        ) : (
          <div className="relative w-64">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchInputChange}
              placeholder={t('Search')}
              className="w-full pl-10 pr-10 py-1 text-xs border border-gray-300 dark:border-gray-500 bg-transparent text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-400 hover:bg-gray-50/50 dark:hover:bg-gray-700/50 transition-all duration-200 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <PiMagnifyingGlassLight className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <button
              onClick={() => {
                setIsSearchOpen(false);
                setSearchQuery('');
                onSearchChange?.('');
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <PiXLight className="h-4 w-4" />
            </button>
          </div>
        )
      )}
      {totalActiveFilters > 0 && onClearAllFilters && (
        <button
          onClick={() => {
            setSearchQuery('');
            setIsSearchOpen(false);
            onClearAllFilters();
          }}
          className={controlBtnClass}
        >
          <PiXLight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
          <span className="text-black dark:text-white text-xs">{t('Clear All')}</span>
        </button>
      )}
    </>
  );

  return (
    <div className={clsx(
      { 'border-b border-gray-200 dark:border-gray-700': !noBorder },
      { 'px-4 pt-3 pb-2': !noParentPadding },
      { 'pb-2 px-2': noParentPadding },
    )}>
      <div className="flex items-center justify-between gap-4">
        {/* ── LEFT SIDE: Tabs (+ controls when no tabs) ── */}
        <div className="flex items-center gap-3">
          {tabs.length > 0 && (
            <div className="flex space-x-2">
              {tabs.map((tab) => renderTab(tab))}
            </div>
          )}
          {!hasLeftTabs && filterSortSearchControls}
        </div>

        {/* ── RIGHT SIDE: (controls when tabs present) → Item Count → rightTabs → View Toggle → Actions → Additional → Refresh ── */}
        {hasControls && (
          <div className="flex items-center gap-3">
            {hasLeftTabs && filterSortSearchControls}
            {itemCount !== undefined && (
              <Badge color="gray">
                {itemCount} of {totalItemCount ?? itemCount} {t(itemCountLabel)}
              </Badge>
            )}
            {rightTabs.length > 0 && (
              <div className="flex space-x-2">
                {rightTabs.map((tab) => renderTab(tab))}
              </div>
            )}
            {viewMode && <ViewToggle />}
            {actions.length > 0 && selectionCount != null && selectionCount > 0 && (
              <Menu as="div" className="relative" data-tour-id="matters-actions">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {selectionCount} {t('selected')}
                  </span>
                  <Menu.Button className="flex gap-1 items-center px-2 py-1 border border-blue-300 dark:border-blue-600 bg-blue-50 text-black dark:text-white hover:bg-blue-100 dark:bg-blue-900 dark:hover:bg-blue-800 transition-all duration-200 rounded shadow-sm">
                  <PiGearLight className="h-4 w-4" />
                  <span className="text-xs">{t('Actions')}</span>
                </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 z-50">
                    {actions.map((action, index) => (
                      <Menu.Item key={index}>
                        {({ active }) => (
                          <div
                            onClick={action.onClick}
                            className={`flex items-center gap-2 p-2 cursor-pointer text-xs text-gray-700 dark:text-gray-200 ${active ? 'bg-gray-100 dark:bg-gray-600' : ''}`}
                          >
                            {action.icon && <span>{action.icon}</span>}
                            {t(action.label)}
                          </div>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
            {additionalControls && <div className="flex items-center gap-3">{additionalControls}</div>}
            {onRefresh && (
              <Button
                variant="outline"
                size="small"
                onClick={onRefresh}
                disabled={isRefreshLoading}
                icon={<PiArrowsClockwiseLight className={clsx('h-4 w-4 ml-1', isRefreshLoading && 'animate-spin')} />}
              >
                {t('Refresh')}
              </Button>
            )}
            {recycleBinEnabled && (
              <button
                type="button"
                onClick={() => onRecycleBinToggle?.(!recycleBinActive)}
                title={recycleBinActive
                  ? t('Back to {{label}}', { label: recycleBinLabel || t('list') })
                  : t('Open recycle bin')}
                className={clsx(
                  'inline-flex items-center gap-1 h-7 px-2 rounded border text-xs transition-colors',
                  recycleBinActive
                    ? 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-600 dark:text-red-300'
                    : 'bg-transparent border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                )}
              >
                <PiTrashLight className="h-4 w-4" />
                {recycleBinActive && (
                  <span>
                    {recycleBinLabel
                      ? t('Back to {{label}}', { label: recycleBinLabel })
                      : t('Recycle Bin')}
                  </span>
                )}
                {typeof recycleBinCount === 'number' && recycleBinCount > 0 && !recycleBinActive && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200">
                    {recycleBinCount}
                  </span>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterBarWithTabs;
